/**
 * Conversation tags (TAGS) — Podium/Intercom-style labels on inbox threads.
 * A managed colored catalog applied many-to-many, with inline create. Chips
 * show on the thread + the list; the inbox can filter by tag.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Tag as TagIcon, Plus, X, Check } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";

export interface ConvTag {
  id: string;
  name: string;
  color: string;
  sort: number;
  archived: boolean;
}

export const TAG_COLORS: Record<string, { chip: string; dot: string }> = {
  slate: { chip: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
  rose: { chip: "bg-rose-100 text-rose-700", dot: "bg-rose-500" },
  amber: { chip: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  emerald: { chip: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  sky: { chip: "bg-sky-100 text-sky-700", dot: "bg-sky-500" },
  indigo: { chip: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-500" },
  violet: { chip: "bg-violet-100 text-violet-700", dot: "bg-violet-500" },
  pink: { chip: "bg-pink-100 text-pink-700", dot: "bg-pink-500" },
};
export const TAG_COLOR_KEYS = Object.keys(TAG_COLORS);
export const colorOf = (c: string) => TAG_COLORS[c] ?? TAG_COLORS.slate;

export function useTags() {
  return useQuery({
    queryKey: ["conv_tags"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ConvTag[]> => {
      const { data, error } = await supabase.from("conversation_tag").select("*").eq("archived", false).order("sort");
      if (error) throw error;
      return (data ?? []) as ConvTag[];
    },
  });
}

/** All applied links → { conversation_key: tag_id[] }. */
export function useTagLinks() {
  return useQuery({
    queryKey: ["conv_tag_links"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<Record<string, string[]>> => {
      const { data, error } = await supabase.from("conversation_tag_link").select("conversation_key, tag_id");
      if (error) throw error;
      const m: Record<string, string[]> = {};
      for (const r of (data ?? []) as { conversation_key: string; tag_id: string }[]) {
        (m[r.conversation_key] ??= []).push(r.tag_id);
      }
      return m;
    },
  });
}

function useTagMutations() {
  const qc = useQueryClient();
  const inval = () => {
    qc.invalidateQueries({ queryKey: ["conv_tag_links"] });
    qc.invalidateQueries({ queryKey: ["conv_tags"] });
  };
  const apply = useMutation({
    mutationFn: async (v: { key: string; tagId: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("conversation_tag_link").insert({ conversation_key: v.key, tag_id: v.tagId, created_by: u.user?.email ?? null });
      if (error && !/duplicate/i.test(error.message)) throw error;
    },
    onSuccess: inval,
  });
  const remove = useMutation({
    mutationFn: async (v: { key: string; tagId: string }) => {
      const { error } = await supabase.from("conversation_tag_link").delete().eq("conversation_key", v.key).eq("tag_id", v.tagId);
      if (error) throw error;
    },
    onSuccess: inval,
  });
  const create = useMutation({
    mutationFn: async (v: { name: string; color: string }): Promise<ConvTag> => {
      const { data, error } = await supabase.from("conversation_tag").insert({ name: v.name.trim(), color: v.color, sort: 999 }).select().single();
      if (error) throw error;
      return data as ConvTag;
    },
    onSuccess: inval,
  });
  return { apply, remove, create };
}

export function TagChip({ tag, onRemove }: { tag: ConvTag; onRemove?: () => void }) {
  const c = colorOf(tag.color);
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium", c.chip)}>
      {tag.name}
      {onRemove && (
        <button type="button" onClick={onRemove} className="opacity-60 transition-opacity hover:opacity-100" title="Remove tag">
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  );
}

/** Thread tag bar — current chips + an add menu (toggle + inline create). */
export function ThreadTagBar({ conversationKey }: { conversationKey: string }) {
  const { data: tags } = useTags();
  const { data: links } = useTagLinks();
  const { apply, remove, create } = useTagMutations();
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const applied = links?.[conversationKey] ?? [];
  const appliedTags = (tags ?? []).filter((t) => applied.includes(t.id));

  const toggle = (t: ConvTag) => {
    if (applied.includes(t.id)) remove.mutate({ key: conversationKey, tagId: t.id });
    else apply.mutate({ key: conversationKey, tagId: t.id });
  };
  const addNew = async () => {
    const name = newName.trim();
    if (!name) return;
    const t = await create.mutateAsync({ name, color: TAG_COLOR_KEYS[(tags?.length ?? 0) % TAG_COLOR_KEYS.length] });
    apply.mutate({ key: conversationKey, tagId: t.id });
    setNewName("");
  };

  return (
    <div className="relative flex flex-wrap items-center gap-1" ref={ref}>
      {appliedTags.map((t) => (
        <TagChip key={t.id} tag={t} onRemove={() => remove.mutate({ key: conversationKey, tagId: t.id })} />
      ))}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-hairline px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-50"
      >
        <TagIcon className="h-3 w-3" /> Tag
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-56 overflow-hidden rounded-lg border border-hairline bg-white py-1 shadow-e2">
          <div className="max-h-56 overflow-y-auto">
            {(tags ?? []).map((t) => {
              const on = applied.includes(t.id);
              return (
                <button key={t.id} type="button" onClick={() => toggle(t)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-slate-50">
                  <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", colorOf(t.color).dot)} />
                  <span className="min-w-0 flex-1 truncate text-slate-700">{t.name}</span>
                  {on && <Check className="h-3.5 w-3.5 shrink-0 text-brand-600" />}
                </button>
              );
            })}
          </div>
          <div className="border-t border-hairline p-2">
            <div className="flex items-center gap-1">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addNew();
                  }
                }}
                placeholder="New tag…"
                className="w-full rounded-md border border-hairline px-2 py-1 text-xs focus:outline-none"
              />
              <button type="button" onClick={addNew} disabled={!newName.trim()} className="rounded-md bg-brand-700 px-2 py-1 text-white transition-colors hover:bg-brand-800 disabled:opacity-40">
                <Plus className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
